import React from 'react';
import { Box, Typography, Paper } from '@mui/material';

function TermsOfUse() {
  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Terms of Use
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Last updated: February 21, 2026
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>1. Acceptance of Terms</Typography>
        <Typography variant="body2" paragraph>
          By accessing or using BlueShift Auto ("the Service"), you agree to be bound by these Terms of Use. If you do not agree to these terms, do not use the Service.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>2. Description of Service</Typography>
        <Typography variant="body2" paragraph>
          BlueShift Auto is a vehicle search and market analysis platform. The Service provides vehicle listing data, market price comparisons, geographic arbitrage analysis, and estimated transport costs to help users make informed vehicle purchasing decisions. The Service may also display advertisements, sponsored listings, and partner offers as part of the user experience.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>3. Disclaimer of Accuracy</Typography>
        <Typography variant="body2" paragraph>
          All market analysis, price estimates, transport cost estimates, and savings calculations provided by the Service are <strong>estimates only</strong>. They are based on aggregated listing data and mathematical models, and do not constitute financial advice, appraisals, or guarantees.
        </Typography>
        <Typography variant="body2" paragraph>
          Specifically:
        </Typography>
        <Box component="ul" sx={{ pl: 2, mb: 2 }}>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Market median prices reflect listing prices, not transaction prices</Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Transport cost estimates are based on straight-line distance and average carrier rates, and may differ significantly from actual quotes</Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Savings estimates do not account for vehicle condition, trim differences, mileage variations, taxes, dealer fees, registration costs, or state-specific regulations</Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Vehicle listings may be outdated, inaccurate, or no longer available</Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Sales tax varies by state (from 0% to over 10%) and is not included in any estimates</Typography>
        </Box>
        <Typography variant="body2" paragraph>
          Always verify pricing, availability, and vehicle condition directly with sellers before making purchasing decisions.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>4. User Accounts</Typography>
        <Typography variant="body2" paragraph>
          You may browse the Service anonymously. Certain features (saved searches, account preferences) require creating an account. You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>5. Acceptable Use</Typography>
        <Typography variant="body2" paragraph>
          You agree not to:
        </Typography>
        <Box component="ul" sx={{ pl: 2, mb: 2 }}>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Use the Service for any unlawful purpose</Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Attempt to scrape, crawl, or programmatically extract data from the Service</Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Interfere with or disrupt the Service or its infrastructure</Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Resell or redistribute data obtained from the Service without permission</Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Create multiple accounts to circumvent usage limits</Typography>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>6. AI-Powered Services</Typography>
        <Typography variant="body2" paragraph>
          The Service may offer AI-powered features including, but not limited to, automated vehicle search and discovery, price negotiation with dealers, offer drafting, inspection coordination, transport arrangement, and transaction management ("AI Services"). These features use artificial intelligence to assist you with the vehicle acquisition process.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Human Approval Required:</strong> AI Services operate under a human-in-the-loop model. No outbound communication (including offers, counteroffers, or commitments) will be sent to any dealer, seller, or third party without your explicit review and approval. You retain full control over all actions taken on your behalf.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>No Binding Authority:</strong> AI-generated communications, offers, and recommendations are prepared on your behalf but do not constitute binding legal agreements unless you separately execute a formal purchase agreement. The AI agent does not have legal authority to enter into contracts on your behalf.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Not Professional Advice:</strong> AI Services do not constitute legal, financial, tax, or professional advice. AI-generated recommendations regarding pricing, negotiation strategy, vehicle condition, or transaction structure are based on data analysis and algorithmic models, and should not be relied upon as a substitute for professional consultation.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Limitations:</strong> AI systems may produce inaccurate, incomplete, or inappropriate outputs. While we implement safeguards including price limits, required inspections, and other hard constraints, you are ultimately responsible for reviewing and approving all AI-recommended actions. BlueShift Auto is not liable for any losses arising from reliance on AI-generated recommendations or communications.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Data Processing:</strong> AI Services require processing your transaction-related data (vehicle preferences, price parameters, communication history) through third-party AI model providers. By using AI Services, you consent to this processing as described in our Privacy Policy.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>7. Advertising and Sponsored Content</Typography>
        <Typography variant="body2" paragraph>
          The Service may display banner advertisements, sponsored vehicle listings, and promotional content from third-party advertising partners. Sponsored listings and ads will be clearly identified as such. BlueShift Auto is not responsible for the accuracy of advertising content or for any transactions between you and advertisers.
        </Typography>
        <Typography variant="body2" paragraph>
          We may also generate leads by sharing non-personal search criteria (such as vehicle make, model, year, and general location) with automotive dealers, sales aggregators, and other business partners. We will not share your personal contact information with these partners without your explicit consent.
        </Typography>
        <Typography variant="body2" paragraph>
          The presence of advertisements or sponsored listings does not constitute an endorsement by BlueShift Auto of any advertiser, dealer, or product. You should independently evaluate any offer or listing before making a purchasing decision.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>8. Third-Party Data</Typography>
        <Typography variant="body2" paragraph>
          Vehicle listing data is sourced from third-party providers including MarketCheck. We do not own or control this data and make no guarantees about its accuracy, completeness, or timeliness. Third-party data is subject to the respective provider's terms and conditions.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>9. Limitation of Liability</Typography>
        <Typography variant="body2" paragraph>
          The Service is provided "as is" without warranties of any kind, either express or implied. BlueShift Auto shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising from your use of the Service or reliance on any information provided by the Service.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>10. Changes to Terms</Typography>
        <Typography variant="body2" paragraph>
          We reserve the right to modify these terms at any time. Continued use of the Service after changes constitutes acceptance of the updated terms.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>11. Contact</Typography>
        <Typography variant="body2" paragraph>
          If you have questions about these terms, please contact us at legal@blueshiftauto.com.
        </Typography>
      </Paper>
    </Box>
  );
}

export default TermsOfUse;
